import { useState, useEffect, useRef } from "react";
import logo from "./img/obuntu.jpg";

const COLORS = {
  rust: "#B84A2E",
  rustLt: "#D4613C",
  earth: "#3B2A1A",
  sand: "#F0E8DC",
  cream: "#FAF7F2",
  warmMid: "#8B6C54",
  white: "#FFFFFF",
  text: "#1C1410",
  muted: "#6B5A4E",
  border: "#D9CEBE",
};

// Google Fonts injected once
function useFonts() {
  useEffect(() => {
    if (document.getElementById("obuntu-fonts")) return;
    const link = document.createElement("link");
    link.id = "obuntu-fonts";
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400;1,600&family=DM+Sans:wght@300;400;500;600&display=swap";
    document.head.appendChild(link);
  }, []);
}

// Fade-up hook
function useFadeUp() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.unobserve(el); } },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

// Reusable animated wrapper
function FadeUp({ children, delay = 0, style = {} }) {
  const [ref, visible] = useFadeUp();
  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(24px)",
        transition: `opacity 0.6s ease ${delay}ms, transform 0.6s ease ${delay}ms`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// Section label
function SectionLabel({ children }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      fontSize: 11, fontWeight: 500, letterSpacing: "0.18em",
      textTransform: "uppercase", color: COLORS.rust, marginBottom: 18,
    }}>
      {children}
      <span style={{ width: 40, height: 1, background: COLORS.rust, flexShrink: 0 }} />
    </div>
  );
}

// ─── NAV ───────────────────────────────────────────────────────────
function Nav() {
  const [menuOpen, setMenuOpen] = useState(false);
  const links = [
    { label: "About Us", href: "#about" },
    { label: "Our History", href: "#history" },
    { label: "The Campaign", href: "#campaign" },
    { label: "Survivor Stories", href: "#stories" },
    { label: "Get Involved", href: "#help" },
    { label: "News", href: "#news" },
  ];

  return (
    <nav style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "0 48px", height: 68,
      background: "rgba(28,20,16,0.96)", backdropFilter: "blur(8px)",
      borderBottom: "1px solid rgba(255,255,255,0.06)",
      fontFamily: "'DM Sans', sans-serif",
    }}>
      {/* Brand */}
      <a href="#" style={{ display: "flex", alignItems: "center", gap: 12, textDecoration: "none" }}>
        <img src={logo} alt="Obuntu Thalidomide Survivors Alliance Africa" style={{ width: 42, height: 42, objectFit: "contain", flexShrink: 0 }} />
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 17, fontWeight: 600, color: COLORS.white, lineHeight: 1.2 }}>
          Obuntu Thalidomide Survivors Alliance
          <span style={{ display: "block", fontSize: 11, fontWeight: 400, fontFamily: "'DM Sans', sans-serif", color: "rgba(255,255,255,0.5)", letterSpacing: "0.08em", textTransform: "uppercase" }}>Africa</span>
        </div>
      </a>

      {/* Desktop links */}
      <ul style={{ display: "flex", alignItems: "center", gap: 28, listStyle: "none", margin: 0, padding: 0 }}
        className="nav-desktop">
        {links.map(l => (
          <li key={l.href}>
            <a href={l.href} style={{ color: "rgba(255,255,255,0.7)", textDecoration: "none", fontSize: 13.5, letterSpacing: "0.03em", transition: "color 0.2s" }}
              onMouseEnter={e => e.target.style.color = "#fff"}
              onMouseLeave={e => e.target.style.color = "rgba(255,255,255,0.7)"}
            >{l.label}</a>
          </li>
        ))}
        <li>
          <a href="#contact" style={{
            background: COLORS.rust, color: COLORS.white, padding: "8px 20px",
            borderRadius: 4, textDecoration: "none", fontSize: 13.5, fontWeight: 500, transition: "background 0.2s",
          }}
            onMouseEnter={e => e.currentTarget.style.background = COLORS.rustLt}
            onMouseLeave={e => e.currentTarget.style.background = COLORS.rust}
          >Contact Us</a>
        </li>
      </ul>

      {/* Hamburger */}
      <button onClick={() => setMenuOpen(o => !o)} style={{ display: "none", flexDirection: "column", gap: 5, background: "none", border: "none", cursor: "pointer", padding: 4 }} className="hamburger">
        {[0,1,2].map(i => <span key={i} style={{ width: 22, height: 2, background: "white", borderRadius: 2, display: "block" }} />)}
      </button>

      {/* Mobile dropdown */}
      {menuOpen && (
        <div style={{
          position: "fixed", top: 68, left: 0, right: 0,
          background: "rgba(28,20,16,0.98)", padding: "24px 32px", zIndex: 99,
          display: "flex", flexDirection: "column", gap: 20,
        }}>
          {[...links, { label: "Contact Us", href: "#contact" }].map(l => (
            <a key={l.href} href={l.href} onClick={() => setMenuOpen(false)}
              style={{ color: "rgba(255,255,255,0.85)", textDecoration: "none", fontSize: 16, fontFamily: "'DM Sans', sans-serif" }}>
              {l.label}
            </a>
          ))}
        </div>
      )}

      <style>{`
        @media (max-width: 980px) {
          .nav-desktop { display: none !important; }
          .hamburger { display: flex !important; }
        }
      `}</style>
    </nav>
  );
}

// ─── HERO ──────────────────────────────────────────────────────────
function Hero() {
  return (
    <section id="hero" style={{
      minHeight: "100vh", background: COLORS.earth,
      display: "flex", alignItems: "center", justifyContent: "center",
      position: "relative", overflow: "hidden", padding: "120px 48px 80px",
      fontFamily: "'DM Sans', sans-serif",
    }}>
      <div style={{
        position: "absolute", inset: 0, opacity: 0.04,
        backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)",
        backgroundSize: "28px 28px",
      }} />
      <div style={{
        position: "absolute", width: 600, height: 600,
        background: "radial-gradient(ellipse, rgba(184,74,46,0.22) 0%, transparent 70%)",
        top: "50%", left: "50%", transform: "translate(-50%,-50%)", pointerEvents: "none",
      }} />

      <div style={{ position: "relative", zIndex: 2, maxWidth: 920, textAlign: "center" }}>
        <div style={{ marginBottom: 24 }}>
          <img src={logo} alt="Obuntu Thalidomide Survivors Alliance Africa logo" style={{ width: 90, height: 90, objectFit: "contain" }} />
        </div>

        <div style={{ display: "inline-flex", alignItems: "center", gap: 14, marginBottom: 32 }}>
          <span style={{ width: 36, height: 1, background: COLORS.rust, flexShrink: 0 }} />
          <span style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: "clamp(20px, 3vw, 32px)",
            fontWeight: 600, letterSpacing: "0.06em", color: COLORS.white, lineHeight: 1.2,
          }}>
            Obuntu Thalidomide Survivors Alliance Africa
          </span>
          <span style={{ width: 36, height: 1, background: COLORS.rust, flexShrink: 0 }} />
        </div>

        <h1 style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: "clamp(52px, 8vw, 96px)",
          fontWeight: 700, color: COLORS.white, lineHeight: 1.0,
          letterSpacing: "-0.02em", margin: "0 0 12px",
        }}>
          <em style={{ fontStyle: "italic", color: COLORS.rust }}>I am</em>{" "}because<br />we are.
        </h1>

        <p style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: "clamp(20px, 2.5vw, 28px)",
          fontWeight: 400, fontStyle: "italic", color: "rgba(255,255,255,0.5)", marginBottom: 32,
        }}>
          Six years of standing together for justice.
        </p>

        <p style={{
          fontSize: 16, lineHeight: 1.7, color: "rgba(255,255,255,0.65)",
          maxWidth: 620, margin: "0 auto 48px",
        }}>
          We are an African-led community of thalidomide survivors, families, and advocates working to ensure every survivor across Africa receives the justice, comprehensive care, and recognition they deserve.
        </p>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16, flexWrap: "wrap" }}>
          <a href="#about"
            style={{ display: "inline-flex", alignItems: "center", gap: 8, background: COLORS.rust, color: COLORS.white, textDecoration: "none", padding: "14px 32px", borderRadius: 4, fontSize: 14, fontWeight: 500, letterSpacing: "0.04em", transition: "background 0.2s" }}
            onMouseEnter={e => e.currentTarget.style.background = COLORS.rustLt}
            onMouseLeave={e => e.currentTarget.style.background = COLORS.rust}
          >Our Story ↓</a>
          <a href="https://thalidomidenetwork.com" target="_blank" rel="noopener"
            style={{ display: "inline-flex", alignItems: "center", gap: 8, border: "1px solid rgba(255,255,255,0.25)", color: "rgba(255,255,255,0.85)", textDecoration: "none", padding: "14px 32px", borderRadius: 4, fontSize: 14, transition: "border-color 0.2s, color 0.2s" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.6)"; e.currentTarget.style.color = "#fff"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.25)"; e.currentTarget.style.color = "rgba(255,255,255,0.85)"; }}
          >↗ Thalidomide Network</a>
        </div>
      </div>
    </section>
  );
}

// ─── BRIDGE BANNER ─────────────────────────────────────────────────
function Bridge() {
  return (
    <div style={{
      background: COLORS.rust, padding: "22px 48px",
      display: "flex", alignItems: "center", justifyContent: "center",
      gap: 20, flexWrap: "wrap", textAlign: "center",
      fontFamily: "'DM Sans', sans-serif",
    }}>
      <p style={{ color: "rgba(255,255,255,0.9)", fontSize: 14, margin: 0 }}>
        We are connected to the wider movement.{" "}
        <strong style={{ color: COLORS.white, fontWeight: 600 }}>Learn about thalidomide survivors across Africa:</strong>
      </p>
      <a href="https://thalidomidenetwork.com" target="_blank" rel="noopener"
        style={{ display: "inline-flex", alignItems: "center", gap: 8, background: COLORS.white, color: COLORS.rust, textDecoration: "none", padding: "9px 22px", borderRadius: 4, fontSize: 13.5, fontWeight: 600, whiteSpace: "nowrap" }}
        onMouseEnter={e => e.currentTarget.style.background = COLORS.sand}
        onMouseLeave={e => e.currentTarget.style.background = COLORS.white}
      >↗ Visit ThalidomideNetwork.com</a>
    </div>
  );
}

// ─── ABOUT ─────────────────────────────────────────────────────────
function About() {
  return (
    <section id="about" style={{ padding: "100px 48px", background: COLORS.white, fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 80, alignItems: "center" }} className="two-col">
          <FadeUp>
            <SectionLabel>About Us</SectionLabel>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(32px,4vw,52px)", fontWeight: 700, lineHeight: 1.1, color: COLORS.earth, marginBottom: 20 }}>
              The heart of the Alliance
            </h2>
            {[
              "We are the Obuntu Thalidomide Survivors Alliance Africa \u2014 an African-led community of thalidomide survivors, families, and advocates united in the pursuit of justice. Our name, Obuntu, embodies the philosophy of \u201cI am because we are,\u201d reflecting our commitment to collective strength and shared humanity.",
              "For six years we have organised, advocated, and stood alongside survivors across the continent, building the case for the recognition and care they have long been denied.",
              "Our vision is simple and unwavering: to ensure every thalidomide survivor in Africa receives the justice, comprehensive care, and recognition they deserve.",
            ].map((text, i) => (
              <p key={i} style={{ fontSize: 15.5, lineHeight: 1.75, color: COLORS.muted, marginTop: i === 0 ? 0 : 16 }}>{text}</p>
            ))}
          </FadeUp>

          <FadeUp delay={120}>
            <div style={{ position: "relative", padding: 48, background: COLORS.earth, borderRadius: 6, color: COLORS.white }}>
              <div style={{ fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase", color: COLORS.rust, marginBottom: 14, fontWeight: 500 }}>Our Headquarters</div>
              <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24, fontStyle: "italic", lineHeight: 1.5, color: COLORS.white, marginBottom: 20 }}>
                Conrad House, 1st Floor<br />Plot 24 Jinja Road<br />Kampala, Uganda
              </p>
              <p style={{ fontSize: 13.5, lineHeight: 1.7, color: "rgba(255,255,255,0.55)", marginBottom: 24 }}>
                Placing us at the heart of the movement for justice in East Africa.
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, borderTop: "1px solid rgba(255,255,255,0.15)", paddingTop: 24 }}>
                <div>
                  <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 40, fontWeight: 700, color: COLORS.rust, lineHeight: 1 }}>6</div>
                  <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "rgba(255,255,255,0.45)", marginTop: 4 }}>Years of Advocacy</div>
                </div>
                <div>
                  <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 40, fontWeight: 700, color: COLORS.rust, lineHeight: 1 }}>Kampala</div>
                  <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "rgba(255,255,255,0.45)", marginTop: 4 }}>East Africa Base</div>
                </div>
              </div>
            </div>
          </FadeUp>
        </div>
      </div>
      <style>{`@media(max-width:900px){.two-col{grid-template-columns:1fr!important;gap:40px!important}}`}</style>
    </section>
  );
}

// ─── HISTORY / LEGACY ──────────────────────────────────────────────
const LEGACY = [
  { icon: "🌍", title: "A Global Scandal", desc: "Developed by German pharmaceutical company Grunenthal and licensed in the UK in 1958, thalidomide was marketed as a safe sedative and cure for morning sickness. It caused a global epidemic of severe birth defects, affecting over 10,000 children across 46 nations." },
  { icon: "📍", title: "The African Dimension", desc: "The tragedy was not confined to the West. Thalidomide reached more than 50 countries worldwide, including 18 in Africa \u2014 widely distributed across Angola, Mozambique, Zimbabwe, Namibia, South Africa, Nigeria, and former British and French colonies. Uganda was among the affected nations." },
  { icon: "⏳", title: "A Longer Shadow", desc: "While Europe withdrew the drug by 1961\u201362, weak regulatory oversight in many African nations meant it remained available for longer, causing additional suffering. In 1962, doctors at Mulago National Referral Hospital in Uganda published an alert in the British Medical Journal about rising cases of phocomelia in Ugandan newborns." },
  { icon: "🕯️", title: "Generations of Neglect", desc: "For over 60 years, African survivors have been left without support, acknowledgment, or compensation \u2014 a stark contrast to their counterparts in Europe, where trusts and foundations were established to compensate victims." },
];

function History() {
  const [hovered, setHovered] = useState(null);
  return (
    <section id="history" style={{ padding: "100px 48px", background: COLORS.earth, fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <FadeUp>
          <SectionLabel>Our History</SectionLabel>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(32px,4vw,52px)", fontWeight: 700, lineHeight: 1.1, color: COLORS.white, marginBottom: 12 }}>
            The unfinished tragedy
          </h2>
          <p style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic", fontSize: 20, color: COLORS.rust, marginBottom: 20 }}>
            "Forgotten to die"
          </p>
          <p style={{ fontSize: 15.5, lineHeight: 1.75, color: "rgba(255,255,255,0.6)", maxWidth: 680 }}>
            Thalidomide's legacy in Africa is a story of a global scandal, decades of neglect, and survivors still waiting to be heard.
          </p>
        </FadeUp>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 2, marginTop: 56 }} className="four-col">
          {LEGACY.map((l, i) => (
            <FadeUp key={l.title} delay={i * 70}>
              <div
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                style={{
                  background: hovered === i ? "rgba(255,255,255,0.09)" : "rgba(255,255,255,0.05)",
                  padding: "32px 26px", borderTop: `2px solid ${COLORS.rust}`,
                  transition: "background 0.2s", height: "100%",
                }}
              >
                <div style={{ fontSize: 26, marginBottom: 14 }}>{l.icon}</div>
                <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 19, fontWeight: 600, color: COLORS.white, marginBottom: 10 }}>{l.title}</h3>
                <p style={{ fontSize: 12.5, lineHeight: 1.65, color: "rgba(255,255,255,0.55)" }}>{l.desc}</p>
              </div>
            </FadeUp>
          ))}
        </div>

        <FadeUp delay={280}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 24, marginTop: 48, paddingTop: 40, borderTop: "1px solid rgba(255,255,255,0.1)" }} className="four-col">
            {[
              { num: "10,000+", label: "Children affected worldwide" },
              { num: "46", label: "Nations impacted globally" },
              { num: "18", label: "African countries reached" },
              { num: "60+", label: "Years without recognition" },
            ].map(s => (
              <div key={s.label} style={{ textAlign: "center" }}>
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 34, fontWeight: 700, color: COLORS.rust, lineHeight: 1 }}>{s.num}</div>
                <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(255,255,255,0.45)", marginTop: 6 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </FadeUp>
      </div>
      <style>{`@media(max-width:900px){.four-col{grid-template-columns:1fr 1fr!important}}`}</style>
      <style>{`@media(max-width:600px){.four-col{grid-template-columns:1fr!important}}`}</style>
    </section>
  );
}

// ─── CAMPAIGN ──────────────────────────────────────────────────────
const DEMANDS = [
  "A meeting with the Ambassadors of the United Kingdom and Germany to establish compensation mechanisms consistent with international law.",
  "Direct communication channels between the Prime Minister's Office and the British and German embassies.",
  "Collaboration with government authorities to compile a comprehensive list of eligible beneficiaries.",
];

function Campaign() {
  return (
    <section id="campaign" style={{ padding: "100px 48px", background: COLORS.cream, fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 80, alignItems: "start" }} className="two-col">
          <FadeUp>
            <SectionLabel>The Fight For Justice</SectionLabel>
            <div style={{
              display: "inline-block", fontSize: 11, fontWeight: 600, letterSpacing: "0.12em",
              textTransform: "uppercase", color: COLORS.white, background: COLORS.rust,
              padding: "6px 14px", borderRadius: 4, marginBottom: 20,
            }}>
              Urgent · Active Campaign
            </div>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(32px,4vw,52px)", fontWeight: 700, lineHeight: 1.1, color: COLORS.earth, marginBottom: 20 }}>
              Survivors demand compensation from the UK and German governments
            </h2>
            <p style={{ fontSize: 15.5, lineHeight: 1.75, color: COLORS.muted }}>
              Thalidomide survivors in Uganda, as part of our Alliance, have formally petitioned Prime Minister Hon. Robbinah Nabbanja, demanding:
            </p>
          </FadeUp>

          <FadeUp delay={100}>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {DEMANDS.map((d, i) => (
                <div key={i} style={{ background: COLORS.white, padding: "24px 28px", borderLeft: `3px solid ${COLORS.rust}` }}>
                  <p style={{ fontSize: 14.5, lineHeight: 1.7, color: COLORS.text, margin: 0 }}>{d}</p>
                </div>
              ))}
              <div style={{ background: COLORS.earth, padding: "28px", marginTop: 16, borderRadius: 6 }}>
                <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: COLORS.rust, marginBottom: 10 }}>The Inequality</div>
                <p style={{ fontSize: 14, lineHeight: 1.7, color: "rgba(255,255,255,0.7)", margin: 0 }}>
                  While British and German governments compensated victims in their own countries and other affected nations, Ugandan survivors have been completely overlooked. This is a fundamental issue of equality and human rights.
                </p>
              </div>
            </div>
          </FadeUp>
        </div>
      </div>
      <style>{`@media(max-width:900px){.two-col{grid-template-columns:1fr!important;gap:40px!important}}`}</style>
    </section>
  );
}

// ─── SURVIVOR STORIES ──────────────────────────────────────────────
const STORIES = [
  { name: "Moses Okello", desc: "Despite facing significant mobility challenges with one arm and no legs, Moses continues to inspire others with his resilience and determination to live independently." },
  { name: "Maria Langole", desc: "Born in 1959, Maria's story is a testament to the power of community support and the urgent need for better care systems." },
  { name: "Shafiq Mugumba", desc: "Despite being born with thalidomide-related disabilities affecting his arms, Shafiq has become an inspiration through his dedication to education and community empowerment." },
];

function Stories() {
  const [hovered, setHovered] = useState(null);
  return (
    <section id="stories" style={{ padding: "100px 48px", background: COLORS.white, fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <FadeUp>
          <SectionLabel>Survivor Stories</SectionLabel>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(32px,4vw,52px)", fontWeight: 700, lineHeight: 1.1, color: COLORS.earth, marginBottom: 20 }}>
            Voices of resilience
          </h2>
          <p style={{ fontSize: 15.5, lineHeight: 1.75, color: COLORS.muted, maxWidth: 680 }}>
            Personal stories are our most powerful tool for connection. These are just a few of the survivors whose courage drives our work.
          </p>
        </FadeUp>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 20, marginTop: 56 }} className="three-col">
          {STORIES.map((s, i) => (
            <FadeUp key={s.name} delay={i * 80}>
              <div
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                style={{
                  border: `1px solid ${hovered === i ? COLORS.rust : COLORS.border}`,
                  padding: "32px 28px", borderRadius: 6,
                  boxShadow: hovered === i ? "0 12px 40px rgba(0,0,0,0.08)" : "none",
                  transform: hovered === i ? "translateY(-3px)" : "translateY(0)",
                  transition: "box-shadow 0.2s, transform 0.2s, border-color 0.2s",
                  height: "100%",
                }}
              >
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24, fontWeight: 700, color: COLORS.earth, marginBottom: 14 }}>{s.name}</div>
                <p style={{ fontSize: 13.5, lineHeight: 1.7, color: COLORS.muted, margin: 0 }}>{s.desc}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
      <style>{`@media(max-width:900px){.three-col{grid-template-columns:1fr!important}}`}</style>
    </section>
  );
}

// ─── HOW YOU CAN HELP ──────────────────────────────────────────────
const HELP_ITEMS = [
  { icon: "📢", title: "Amplify Our Voice", desc: "Share our stories using #ObuntuAlliance." },
  { icon: "✍️", title: "Advocate", desc: "Write to your government representatives \u2014 urge them to pressure the UK and German governments to fulfill their moral obligations to African survivors." },
  { icon: "💛", title: "Donate", desc: "Financial support is critical for our advocacy, legal efforts, and to provide direct support to our members." },
  { icon: "🤝", title: "Partner With Us", desc: "If your organization can offer legal, medical, or advocacy support, we welcome your partnership." },
];

function Help() {
  const [hovered, setHovered] = useState(null);
  return (
    <section id="help" style={{ padding: "100px 48px", background: COLORS.sand, fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <FadeUp>
          <SectionLabel>How You Can Help</SectionLabel>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(32px,4vw,52px)", fontWeight: 700, lineHeight: 1.1, color: COLORS.earth, marginBottom: 20 }}>
            A call to action for the world
          </h2>
        </FadeUp>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, marginTop: 48 }} className="four-col">
          {HELP_ITEMS.map((h, i) => (
            <FadeUp key={h.title} delay={i * 70}>
              <div
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                style={{
                  background: COLORS.white,
                  border: `1px solid ${hovered === i ? COLORS.rust : COLORS.border}`,
                  borderRadius: 6, padding: "28px 24px", height: "100%",
                  transition: "border-color 0.2s, transform 0.15s",
                  transform: hovered === i ? "translateY(-3px)" : "translateY(0)",
                }}
              >
                <div style={{ width: 44, height: 44, background: COLORS.rust, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, marginBottom: 16 }}>{h.icon}</div>
                <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 19, fontWeight: 600, color: COLORS.earth, marginBottom: 10 }}>{h.title}</h3>
                <p style={{ fontSize: 13, lineHeight: 1.6, color: COLORS.muted, margin: 0 }}>{h.desc}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
      <style>{`@media(max-width:900px){.four-col{grid-template-columns:1fr 1fr!important}}`}</style>
      <style>{`@media(max-width:600px){.four-col{grid-template-columns:1fr!important}}`}</style>
    </section>
  );
}

// ─── NEWS & UPDATES ────────────────────────────────────────────────
const NEWS = [
  { icon: "📰", title: "Petition handed over to Uganda's Prime Minister", desc: "The Alliance formally delivered its petition demanding compensation talks with the UK and German governments." },
  { icon: "🔬", title: "New research on thalidomide's impact in Africa", desc: "Ongoing historical and medical research is documenting the scale of thalidomide's reach across the continent." },
  { icon: "📅", title: "Upcoming advocacy events", desc: "Watch this space for upcoming meetings, protests, and advocacy events across the region." },
];

function News() {
  return (
    <section id="news" style={{ padding: "100px 48px", background: COLORS.white, fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <FadeUp>
          <SectionLabel>News & Updates</SectionLabel>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(32px,4vw,52px)", fontWeight: 700, lineHeight: 1.1, color: COLORS.earth, marginBottom: 20 }}>
            Staying connected to our progress
          </h2>
        </FadeUp>

        <div style={{ display: "flex", flexDirection: "column", gap: 2, marginTop: 40 }}>
          {NEWS.map((n, i) => (
            <FadeUp key={n.title} delay={i * 80}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 20, padding: "24px 4px", borderBottom: `1px solid ${COLORS.border}` }}>
                <div style={{ fontSize: 22, flexShrink: 0 }}>{n.icon}</div>
                <div>
                  <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 19, fontWeight: 600, color: COLORS.earth, marginBottom: 6 }}>{n.title}</div>
                  <p style={{ fontSize: 13.5, lineHeight: 1.6, color: COLORS.muted, margin: 0 }}>{n.desc}</p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── CONTACT / CTA ─────────────────────────────────────────────────
function Contact() {
  return (
    <section id="contact" style={{ padding: "100px 48px", background: COLORS.rust, textAlign: "center", fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <FadeUp>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, fontSize: 11, fontWeight: 500, letterSpacing: "0.18em", textTransform: "uppercase", color: "rgba(255,255,255,0.7)", marginBottom: 18 }}>
            Contact Us
            <span style={{ width: 40, height: 1, background: "rgba(255,255,255,0.4)" }} />
          </div>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(32px,4vw,52px)", fontWeight: 700, lineHeight: 1.1, color: COLORS.white, marginBottom: 20 }}>
            Add your voice to the Alliance
          </h2>
          <p style={{ fontSize: 15.5, lineHeight: 1.75, color: "rgba(255,255,255,0.75)", maxWidth: 680, margin: "0 auto 32px" }}>
            Whether you are a survivor, an ally, a healthcare professional, or an organization \u2014 your participation strengthens our collective voice.
          </p>

          <div style={{ display: "flex", justifyContent: "center", gap: 40, flexWrap: "wrap", marginBottom: 40, fontSize: 14, color: "rgba(255,255,255,0.9)" }}>
            <div>
              <div style={{ fontWeight: 600, color: COLORS.white, marginBottom: 4 }}>Address</div>
              Conrad House, 1st Floor, Plot 24 Jinja Road, Kampala, Uganda
            </div>
            <div>
              <div style={{ fontWeight: 600, color: COLORS.white, marginBottom: 4 }}>Email</div>
              <a href="mailto:obuntuthalid@gmail.com" style={{ color: "rgba(255,255,255,0.9)" }}>obuntuthalid@gmail.com</a>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16, flexWrap: "wrap" }}>
            <a href="mailto:obuntuthalid@gmail.com"
              style={{ display: "inline-flex", alignItems: "center", gap: 8, background: COLORS.white, color: COLORS.rust, textDecoration: "none", padding: "14px 32px", borderRadius: 4, fontSize: 14, fontWeight: 600 }}
              onMouseEnter={e => e.currentTarget.style.background = COLORS.sand}
              onMouseLeave={e => e.currentTarget.style.background = COLORS.white}
            >✉ Contact Us</a>
            <a href="https://thalidomidenetwork.com" target="_blank" rel="noopener"
              style={{ display: "inline-flex", alignItems: "center", gap: 8, border: "2px solid rgba(255,255,255,0.4)", color: COLORS.white, textDecoration: "none", padding: "13px 32px", borderRadius: 4, fontSize: 14 }}
              onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(255,255,255,0.8)"}
              onMouseLeave={e => e.currentTarget.style.borderColor = "rgba(255,255,255,0.4)"}
            >↗ Visit Thalidomide Network</a>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

// ─── FOOTER ────────────────────────────────────────────────────────
function Footer() {
  const cols = [
    { heading: "About", links: [{ label: "About Us", href: "#about" }, { label: "Our History", href: "#history" }, { label: "The Campaign", href: "#campaign" }, { label: "Survivor Stories", href: "#stories" }] },
    { heading: "Get Involved", links: [{ label: "How You Can Help", href: "#help" }, { label: "News & Updates", href: "#news" }, { label: "Partner With Us", href: "#help" }, { label: "Contact Us", href: "#contact" }] },
    { heading: "Movement", links: [{ label: "Thalidomide Network", href: "https://thalidomidenetwork.com", external: true }, { label: "Email Us", href: "mailto:obuntuthalid@gmail.com" }, { label: "Conrad House, Kampala", href: "#contact" }] },
  ];

  return (
    <footer style={{ background: COLORS.earth, color: "rgba(255,255,255,0.55)", padding: "72px 48px 40px", fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 60, paddingBottom: 48, borderBottom: "1px solid rgba(255,255,255,0.1)", marginBottom: 32 }} className="footer-grid">
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
              <img src={logo} alt="OTSA logo" style={{ width: 40, height: 40, objectFit: "contain" }} />
              <div>
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 600, color: COLORS.white, lineHeight: 1.2 }}>Obuntu Thalidomide Survivors Alliance</div>
                <div style={{ fontSize: 12, color: COLORS.rust, letterSpacing: "0.08em", textTransform: "uppercase" }}>Africa</div>
              </div>
            </div>
            <p style={{ fontSize: 13, lineHeight: 1.7, maxWidth: 280, margin: 0 }}>
              An African-led community of thalidomide survivors, families, and advocates \u2014 six years into the pursuit of justice, dignity, and recognition across Africa.
            </p>
          </div>
          {cols.map(col => (
            <div key={col.heading}>
              <h4 style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(255,255,255,0.9)", marginBottom: 16, fontWeight: 500 }}>{col.heading}</h4>
              <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {col.links.map(l => (
                  <li key={l.label} style={{ marginBottom: 10 }}>
                    <a href={l.href} target={l.external ? "_blank" : undefined} rel={l.external ? "noopener" : undefined}
                      style={{ color: "rgba(255,255,255,0.5)", textDecoration: "none", fontSize: 13, transition: "color 0.2s" }}
                      onMouseEnter={e => e.currentTarget.style.color = "#fff"}
                      onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.5)"}
                    >{l.label}</a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <p style={{ fontSize: 12.5, margin: 0 }}>
            © 2026 Obuntu Thalidomide Survivors Alliance Africa · Conrad House, Kampala, Uganda
          </p>
          <span style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic", fontSize: 14, color: "rgba(255,255,255,0.35)" }}>
            &ldquo;I am because we are.&rdquo;
          </span>
        </div>
      </div>
      <style>{`
        @media(max-width:900px){
          .footer-grid{grid-template-columns:1fr 1fr!important;gap:40px!important}
        }
        @media(max-width:600px){
          .footer-grid{grid-template-columns:1fr!important}
        }
      `}</style>
    </footer>
  );
}

// ─── APP ───────────────────────────────────────────────────────────
export default function App() {
  useFonts();
  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", background: COLORS.cream, color: COLORS.text, overflowX: "hidden" }}>
      <Nav />
      <Hero />
      <Bridge />
      <About />
      <History />
      <Campaign />
      <Stories />
      <Help />
      <News />
      <Contact />
      <Footer />
    </div>
  );
}